/* eslint class-methods-use-this: "off", no-console: "off" */
import Analytics from '../../lib/analytics/analytics';
import DigitalData from '../../lib/analytics/my-digital-data';
import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';

// ITEM_TYPE from MYEE src/jsx/malo/constants
export const ITEM_TYPE = {
  EVENT: 'EVENT',
  SERIES: 'SERIES',
  UPSELL: 'UPSELL',
  DONATION: 'DONATION',
  PARKING: 'PARKING',
  INSURANCE: 'INSURANCE',
  PARENT: 'PARENT',
};

const EMPTY_OBJECT_WITH_ID = { id: '' };

const DD_SEPARATOR = ': ';
const TRACK_STRING = 'mytm-ticket-listings';
const GLOBAL_NAVIGATION = 'Global Navigation';
const PAGE_NAVIGATION = 'Page Navigation';

// Singleton
let instance;
let digitalData;
let trackString;

function callbackWithTimeout(fn, timeout = 500) {
  let fnCalled = false;

  function eventCallback() {
    if (!fnCalled) {
      fnCalled = true;
      fn();
    }
  }

  setTimeout(eventCallback, timeout);

  return eventCallback;
}

export class MyAnalytics extends Analytics {
  constructor(ts) {
    super();

    if (!instance) {
      instance = this;
      digitalData = new DigitalData();
      trackString = ts || TRACK_STRING;
    }

    if (!digitalData.get('page.pageInfo.pageChannel')) {
      const domain = digitalData.get('page.pageInfo.domain');

      digitalData.set([
        [ 'page.pageInfo.pageChannel', `${domain}: My Ticketmaster` ],
      ]);
    }

    return instance;
  }

  populateFromStore(store) {
    if (!digitalData || !digitalData.set) {
      return;
    }

    this.store = store;

    const order = get(store, 'order.order', EMPTY_OBJECT_WITH_ID);

    const childEventsArray = filter(order.items, item => item.type === ITEM_TYPE.EVENT);

    const eventInfo = childEventsArray.length > 1
      ? find(order.items, item => item.type === ITEM_TYPE.PARENT) || EMPTY_OBJECT_WITH_ID
      : find(order.items, item => item.type === ITEM_TYPE.EVENT) || EMPTY_OBJECT_WITH_ID;

    digitalData.set([
      [ 'page.attributes.venueID', get(eventInfo, 'venue.id', '') ],
      [ 'page.attributes.venueName', get(eventInfo, 'venue.name', '') ],
      [ 'page.attributes.venueCity', get(eventInfo, 'venue.location.city', '') ],
      [ 'page.attributes.venueRegion', get(eventInfo, 'venue.location.region', '') ],
      [ 'page.attributes.artistID', get(eventInfo, 'attractions[0].id') ],
      [ 'page.attributes.eventDateStart', get(eventInfo, 'dateRange.startDate', '') ],
      [ 'page.attributes.eventDateEnd', get(eventInfo, 'dateRange.endDate', '') ],
      [ 'page.category', get(eventInfo, 'attractions[0].majorCategory.id') ],
      [ 'page.attributes.eventID', eventInfo.id ],
      [ 'page.attributes.eventName', eventInfo.name ],
      [ 'transaction.orderID', order.id ],
    ]);
  }

  trackNavigation(category, source, label, fn) {
    const cb = typeof fn === 'function' ? callbackWithTimeout(fn, 500) : undefined;

    this.trackEvent({
      category,
      action: source,
      label,
      hitCallback: cb,
    });
  }

  trackGlobalNav(source, fn) {
    this.trackNavigation(GLOBAL_NAVIGATION, source, undefined, fn);
  }

  trackPageNav(source, fn) {
    this.trackNavigation(PAGE_NAVIGATION, source, undefined, fn);
  }

  trackPage(name, data) {
    this.setPageName(name);

    digitalData.set([
      [ 'page.attributes.venueID', get(data, 'event._embedded.venues[0].id', '') ],
      [ 'page.attributes.artistID', get(data, 'event._embedded.attractions[0].id', '') ],
      [ 'page.attributes.eventID', get(data, 'event.id', '') ],
      [ 'transaction.orderID', get(data, 'orderInfo.alternateOrderId', '') ],
    ]);

    try {
      this.satellite.track(trackString);
    } catch (e) {
      console.warn('satellite not available?');
      console.warn(e);
    }
  }

  setPageName(name) {
    const channel = digitalData.get('page.pageInfo.pageChannel');
    const channelAndName = [ channel, name ].join(DD_SEPARATOR);
    const domain = digitalData.get('pageInstanceID').replace(/[\s_]/g, '');
    const pageType = `my${domain}_${name.replace(/[\s-]/g, '')}`.toLowerCase();

    digitalData.set([
      [ 'page.pageInfo.pageName', channelAndName ],
      [ 'page.pageInfo.pageID', channelAndName ],
    ]);

    if (window.monetateQ && window.monetateQ.push) {
      window.monetateQ.push([ 'setPageType', pageType ]);
      window.monetateQ.push([ 'trackData' ]);
    }
  }
}

/*
 Analytics, MyAnalytics and DigitalData can have only single instance, so
 we init them here to use everywhere these instances
  */

window.digitalData = window.digitalData || {};
const ticketListingsDigitalData = new DigitalData();
ticketListingsDigitalData.init(window.digitalData, window.siteContext);

const ticketListingsAnalytics = new MyAnalytics();

export default ticketListingsAnalytics;
